// Tela de Cadastro de Funcionário (issue #8) — padrão barra + tabela + modal.

import { newId } from "../../domain/ids.ts";
import type { Funcionario } from "../../domain/snapshot.ts";
import { formatData } from "../../domain/datas.ts";
import type { Store } from "../store.ts";
import { clear, el, tabelaPaginada, botao } from "../ui.ts";
import {
  abrirFormModal,
  barraCadastro,
  campo,
  campoObrigatorio,
  inputData,
  inputTexto,
  telaLista,
} from "./_cadastro.ts";
import { listaSelect } from "./_lista-select.ts";
import { EstadoLista } from "./_estado-lista.ts";
import { buscaDaLista, filtroMultiplo, SITUACAO_CADASTRO } from "./_lista-filtros.ts";
import { podeEditarModulo } from "./_permissao.ts";

export function renderFuncionarios(
  container: HTMLElement,
  store: Store,
): () => void {
  // filtros, busca e página num estado só (#93): null = todos
  const lista = new EstadoLista({ funcao: null as string[] | null, situacao: null as string[] | null });
  const busca = buscaDaLista(lista, "Buscar por nome, função ou contato…", () => render());
  const fSituacao = filtroMultiplo(lista, "situacao", "Situação", () => render());
  const fFuncao = filtroMultiplo(lista, "funcao", "Função", () => render());

  const render = (): void => {
    clear(container);
    const snap = store.get();
    const podeGravar = podeEditarModulo(store, "configuracao");

    fFuncao.atualizar(snap.listas.funcoesFuncionario.map((v) => ({ id: v, nome: v })));
    fSituacao.atualizar(SITUACAO_CADASTRO);
    const funcaoFiltro = fFuncao.selecionados();
    const sitFiltro = fSituacao.selecionados();
    const visiveis = [...snap.funcionarios]
      .filter((f) => funcaoFiltro.size === 0 || funcaoFiltro.has(f.funcao))
      .filter((f) => sitFiltro.size === 0 || sitFiltro.has(f.ativo ? "ativo" : "inativo"))
      .filter((f) => busca.casa(f.nome, f.funcao, f.contato))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

    const barra = barraCadastro(
      [fFuncao.element, fSituacao.element],
      "Novo Funcionário",
      () => abrirModal(null),
      () => {
        lista.limpar();
        render();
      },
      undefined,
      podeGravar,
    );

    if (visiveis.length === 0) {
      container.append(
        telaLista(
          barra,
          busca.elemento(),
          el(
            "p",
            { class: "muted lista-vazia" },
            lista.mensagemVazio({ nenhum: "Nenhum funcionário.", filtrado: "Nenhum funcionário encontrado." }),
          ),
        ),
      );
      return;
    }
    const linhas = visiveis.map((f) => {
      const acoes: Node[] = [];
      if (podeGravar) {
        const editar = botao("secundario", {}, "Editar");
        editar.addEventListener("click", () => abrirModal(f));
        const alternar = botao(
          "secundario",
          {},
          f.ativo ? "Inativar" : "Reativar",
        );
        alternar.addEventListener("click", () => {
          void store.executar(
            {
              agregado: "funcionario",
              cmd: { tipo: "definirAtivo", id: f.id, ativo: !f.ativo },
            },
            f.ativo ? "Funcionário inativado." : "Funcionário reativado.",
          );
        });
        acoes.push(editar, alternar);
      }
      return el(
        "tr",
        { class: f.ativo ? "" : "inativo" },
        el("td", {}, f.nome),
        el("td", { class: "muted" }, f.funcao || "—"),
        el("td", { class: "muted" }, f.contato || "—"),
        el(
          "td",
          { class: "muted", "data-sort": f.admissao ?? "" },
          f.admissao ? formatData(f.admissao) : "—",
        ),
        el(
          "td",
          {},
          f.ativo
            ? el("span", { class: "estado-ativo" }, "Ativo")
            : el("span", { class: "estado-inativo" }, "Inativo"),
        ),
        el("td", { class: "acoes" }, ...acoes),
      );
    });
    container.append(
      telaLista(
        barra,
        busca.elemento(),
        tabelaPaginada(
          "funcionarios",
          ["Nome", "Função", "Contato", "Admissão", "Situação", ""],
          linhas,
          lista.tabela,
          render,
        ),
      ),
    );
  };

  const abrirModal = (atual: Funcionario | null): void => {
    const nome = inputTexto("Nome do funcionário", atual?.nome ?? "");
    const funcao = listaSelect(store, "funcoesFuncionario", atual?.funcao ?? "");
    const contato = inputTexto("Telefone / contato", atual?.contato ?? "");
    const admissao = inputData(atual?.admissao ?? "");

    abrirFormModal({
      titulo: atual ? `Editar ${atual.nome}` : "Novo Funcionário",
      rotuloSalvar: atual ? "Salvar" : "Criar Funcionário",
      campos: () =>
        el(
          "div",
          {},
          campoObrigatorio("Nome", nome),
          campo("Função", funcao.element),
          campo("Contato", contato),
          campo("Data de admissão", admissao),
        ),
      salvar: (aoErro) => {
        const dados = {
          nome: nome.value,
          funcao: funcao.value(),
          contato: contato.value,
          admissao: admissao.value,
        };
        const id = atual?.id ?? newId();
        const cmd = atual
          ? ({ tipo: "editar", id, dados } as const)
          : ({ tipo: "criar", id, dados } as const);
        return store.executar({ agregado: "funcionario", cmd }, undefined, aoErro);
      },
    });
  };

  render();
  return store.subscribe(render);
}
